import { useState, useEffect, useRef } from "react";
import { AIFlowchartContent } from "../../types/ai-templates";

export function FlowchartTemplate4({
  content,
  onUpdate,
}: {
  content: AIFlowchartContent;
  onUpdate: (newContent: AIFlowchartContent) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [aspectRatio] = useState<number>(744 / 600); // Original SVG aspect ratio

  // Maintain aspect ratio on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = width / aspectRatio;
        containerRef.current.style.height = `${height}px`;
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  }, [aspectRatio]);

  // Define colors
  const colors = {
    background: "#ffffff",
    blue: "#4f91fc",
    green: "#43dd93",
    red: "#fb6762",
    lightGreen: "#a6da37",
    textHeading: "#171717",
    textDescription: "#555",
  };

  // Define quadrant colors
  const quadrantColors = {
    topLeft: {
      fill: "#3e4e69",
      stroke: colors.blue,
      animatedStroke: "#0060fb7c",
    },
    topRight: {
      fill: "#3b5649",
      stroke: colors.green,
      animatedStroke: "#00c5676e",
    },
    bottomLeft: {
      fill: "#68403f",
      stroke: colors.red,
      animatedStroke: "#f9080063",
    },
    bottomRight: {
      fill: "#4b533a",
      stroke: colors.lightGreen,
      animatedStroke: "#6b9d0056",
    },
  };

  // Get color for step based on index
  const getStepColor = (index: number) => {
    switch (index % 4) {
      case 0:
        return colors.blue;
      case 1:
        return colors.green;
      case 2:
        return colors.red;
      case 3:
        return colors.lightGreen;
      default:
        return colors.blue;
    }
  };

  // Get quadrant title
  const getQuadrantTitle = (index: number, step: any): string => {
    const defaultTitles = {
      0: "Diverse Perspectives",
      1: "Comprehensive Evaluation",
      2: "Individual Bias",
      3: "Mitigated Bias",
    };
    return step.title || defaultTitles[index % 4] || `Step ${index + 1}`;
  };

  // Format title display for multi-line titles
  const formatTitleDisplay = (index: number, title: string) => {
    if (index === 0) {
      const parts = title.split(" ");
      if (parts.length > 1) {
        return (
          <>
            <div className="text-center mb-1">{parts[0]}</div>
            <div>{parts.slice(1).join(" ")}</div>
          </>
        );
      }
    } else if (index === 1) {
      const parts = title.split(" ");
      if (parts.length > 1) {
        return (
          <>
            <div className="mb-1">{parts[0]}</div>
            <div>{parts.slice(1).join(" ")}</div>
          </>
        );
      }
    }
    return title;
  };

  // Handle content update
  const handleContentUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  // Render title
  const renderTitle = () => {
    const title = content.title || "Benefits of Panel Interviews";
    return (
      <div
        className="absolute left-1/2 transform -translate-x-1/2"
        style={{ top: "8%" }}
      >
        <h2
          className="text-xl md:text-2xl font-normal text-center"
          style={{ color: colors.textHeading }}
        >
          {title}
        </h2>
      </div>
    );
  };

  // Render quadrant text
  const renderQuadrantText = (index: number) => {
    if (index >= content.steps.length) return null;

    const step = content.steps[index];
    const stepColor = getStepColor(index);
    const isEditingTitle = editingIndex === index && editingField === "title";
    const isEditingDescription =
      editingIndex === index && editingField === "description";

    let titlePos, descPos;
    switch (index % 4) {
      case 0: // Top-left
        titlePos = { left: "10%", top: "20%" };
        descPos = { left: "5%", top: "30%" };
        break;
      case 1: // Top-right
        titlePos = { left: "68%", top: "20%" };
        descPos = { left: "68%", top: "30%" };
        break;
      case 2: // Bottom-left
        titlePos = { left: "8%", top: "65%" };
        descPos = { left: "7%", top: "71%" };
        break;
      case 3: // Bottom-right
        titlePos = { left: "68%", top: "65%" };
        descPos = { left: "68%", top: "71%" };
        break;
      default:
        titlePos = { left: "0", top: "0" };
        descPos = { left: "0", top: "0" };
    }

    const titleText = getQuadrantTitle(index, step);

    return (
      <>
        {/* Title */}
        <div
          className="absolute"
          style={{
            left: titlePos.left,
            top: titlePos.top,
            zIndex: 10,
            maxWidth: "180px",
          }}
        >
          {isEditingTitle ? (
            <input
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== titleText) {
                  handleContentUpdate(index, "title", tempValue);
                }
              }}
              className="text-base md:text-lg font-bold bg-transparent border border-gray-300 px-2 py-1 w-full"
              style={{ color: stepColor }}
            />
          ) : (
            <h3
              className="text-base md:text-xl font-bold cursor-pointer"
              style={{ color: stepColor }}
              onClick={() => {
                setEditingIndex(index);
                setEditingField("title");
                setTempValue(titleText);
              }}
            >
              {formatTitleDisplay(index, titleText)}
            </h3>
          )}
        </div>

        {/* Description */}
        <div
          className="absolute"
          style={{
            left: descPos.left,
            top: descPos.top,
            zIndex: 10,
            maxWidth: "180px",
          }}
        >
          {isEditingDescription ? (
            <textarea
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== step.description) {
                  handleContentUpdate(index, "description", tempValue);
                }
              }}
              className="text-xs md:text-sm bg-transparent border border-gray-300 px-2 py-1 w-full"
              style={{ color: colors.textDescription }}
              onFocus={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
            />
          ) : (
            <p
              className="text-xs md:text-sm cursor-pointer leading-relaxed"
              style={{ color: colors.textDescription }}
              onClick={() => {
                setEditingIndex(index);
                setEditingField("description");
                setTempValue(step.description || "");
              }}
            >
              {step.description}
            </p>
          )}
        </div>
      </>
    );
  };

  // Render number in circle
  const renderNumber = (index: number) => {
    if (index >= content.steps.length) return null;

    const stepColor = getStepColor(index);
    let numPos;
    switch (index) {
      case 0:
        numPos = { left: "40%", top: "41%" };
        break;
      case 1:
        numPos = { left: "54%", top: "41%" };
        break;
      case 2:
        numPos = { left: "40%", top: "57%" };
        break;
      case 3:
        numPos = { left: "54%", top: "57%" };
        break;
      default:
        numPos = { left: "0", top: "0" };
    }

    return (
      <div
        className="absolute"
        style={{ left: numPos.left, top: numPos.top, zIndex: 10 }}
      >
        <div
          className="font-bold text-xl md:text-3xl"
          style={{ color: stepColor }}
        >
          {index + 1}
        </div>
      </div>
    );
  };

  // Render quadrant shapes
  const renderQuadrantShapes = () => {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 744 600"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          fillRule: "evenodd",
          clipRule: "evenodd",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeMiterlimit: 1.5,
        }}
      >
        {/* Background rectangle */}
        <g transform="translate(11.5, 12)">
          <rect width="721" height="576" fill={colors.background} />
        </g>

        {/* Connection lines */}
        <g transform="translate(25.5, 98)">
          <path
            d="M 347 10L 347 118M 347 334L 347 442M 683 226L 455 226M 239 226L 10 226"
            stroke="#b7b7b7"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M 347 10L 347 118M 347 334L 347 442M 683 226L 455 226M 239 226L 10 226"
            stroke="#0000001b"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Bottom-right (Quadrant 4) */}
        <g transform="translate(360.19, 298.33)">
          <path
            d="M 10 116.526C 21.424 128.333 37.4359 135.673 55.1621 135.673C 89.8657 135.673 117.999 107.5399 117.999 72.8364C 117.999 38.1328 89.8657 10.000003 55.1621 10C 38.1012 9.999999 22.6284 16.7994 11.3056 27.8362C 22.2469 39.1439 28.9798 54.5486 28.9798 71.5259C 28.9798 89.1686 21.7089 105.113 10 116.526Z"
            fill={quadrantColors.bottomRight.fill}
          />
          <path
            d="M 10 116.5261C 21.424 128.3327 37.4359 135.6727 55.1621 135.6727C 89.8657 135.6727 117.9985 107.5399 117.9985 72.8364C 117.9985 38.1328 89.8657 10 55.1621 10C 38.1012 9.999999 22.6284 16.7994 11.3056 27.8362C 22.2469 39.1439 28.9798 54.5486 28.9798 71.5259C 28.9798 89.1686 21.7089 105.113 10 116.5261ZM 66.306641 115.671875C 66.306641 112.358175 63.620341 109.671875 60.306641 109.671875C 56.992941 109.671875 54.306641 112.358175 54.306641 115.671875C 54.306641 118.985575 56.992941 121.671875 60.306641 121.671875C 63.620341 121.671875 66.306641 118.985575 66.306641 115.671875ZM 62.306641 121.171875L 72.306641 151.671875L 144.306641 151.671875"
            stroke={quadrantColors.bottomRight.stroke}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 10 116.5261C 21.424 128.3327 37.4359 135.6727 55.1621 135.6727C 89.8657 135.6727 117.9985 107.5399 117.9985 72.8364C 117.9985 38.1328 89.8657 10 55.1621 10C 38.1012 9.999999 22.6284 16.7994 11.3056 27.8362C 22.2469 39.1439 28.9798 54.5486 28.9798 71.5259C 28.9798 89.1686 21.7089 105.113 10 116.5261ZM 66.306641 115.671875C 66.306641 112.358175 63.620341 109.671875 60.306641 109.671875C 56.992941 109.671875 54.306641 112.358175 54.306641 115.671875C 54.306641 118.985575 56.992941 121.671875 60.306641 121.671875C 63.620341 121.671875 66.306641 118.985575 66.306641 115.671875ZM 62.306641 121.171875L 72.306641 151.671875L 144.306641 151.671875"
            stroke={quadrantColors.bottomRight.animatedStroke}
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Bottom-left (Quadrant 3) */}
        <g transform="translate(218.5, 314.69)">
          <path
            d="M 64.1452 10C 75.5583 21.7086 91.5025 28.9794 109.145 28.9794C 126.123 28.9794 141.5283 22.2459 152.836 11.3038C 163.873 22.6267 170.673 38.0997 170.673 55.1608C 170.673 89.8644 142.5399 117.997 107.8364 117.997C 73.1328 117.997 45 89.8644 45 55.1608C 45 37.4352 52.3395 21.4239 64.1452 10Z"
            fill={quadrantColors.bottomLeft.fill}
          />
          <path
            d="M 64.1452 10C 75.5583 21.7086 91.5025 28.9794 109.145 28.9794C 126.123 28.9794 141.5283 22.2459 152.8361 11.3038C 163.8732 22.6267 170.6727 38.0997 170.6727 55.1608C 170.6727 89.8644 142.5399 117.9972 107.8364 117.9972C 73.1328 117.9972 45 89.8644 45 55.1608C 45 37.4352 52.3395 21.4239 64.1452 10ZM 88 99.306641C 88 95.992941 90.6863 93.306641 94 93.306641C 97.3137 93.306641 100 95.992941 100 99.306641C 100 102.620341 97.3137 105.306641 94 105.306641C 90.6863 105.306641 88 102.620341 88 99.306641ZM 92 104.806641L 82 135.306641L 10 135.306641"
            stroke={quadrantColors.bottomLeft.stroke}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 64.1452 10C 75.5583 21.7086 91.5025 28.9794 109.145 28.9794C 126.123 28.9794 141.5283 22.2459 152.8361 11.3038C 163.8732 22.6267 170.6727 38.0997 170.6727 55.1608C 170.6727 89.8644 142.5399 117.9972 107.8364 117.9972C 73.1328 117.9972 45 89.8644 45 55.1608C 45 37.4352 52.3395 21.4239 64.1452 10ZM 88 99.306641C 88 95.992941 90.6863 93.306641 94 93.306641C 97.3137 93.306641 100 95.992941 100 99.306641C 100 102.620341 97.3137 105.306641 94 105.306641C 90.6863 105.306641 88 102.620341 88 99.306641ZM 92 104.806641L 82 135.306641L 10 135.306641"
            stroke={quadrantColors.bottomLeft.animatedStroke}
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Top-right (Quadrant 2) */}
        <g transform="translate(343.83, 188)">
          <path
            d="M 116.527 139.305594C 128.333 127.882194 135.673 111.870694 135.673 94.144994C 135.673 59.441394 107.5399 31.308594 72.8364 31.308594C 38.1328 31.308594 10 59.441394 10 94.144994C 10 111.205794 16.7994 126.678594 27.8361 138.001594C 39.1439 127.059494 54.5491 120.326194 71.5269 120.326194C 89.1696 120.326194 105.1141 127.597194 116.527 139.305594Z"
            fill={quadrantColors.topRight.fill}
          />
          <path
            d="M 116.5272 139.306094C 128.3331 127.882194 135.6727 111.870694 135.6727 94.144994C 135.6727 59.441394 107.5399 31.308594 72.8364 31.308594C 38.1328 31.308594 10 59.441394 10 94.144994C 10 111.205794 16.7993 126.678594 27.8361 138.001394C 39.1439 127.059494 54.5491 120.326194 71.5269 120.326194C 89.1696 120.326194 105.1141 127.597194 116.5272 139.306094ZM 82.672852 46C 82.672852 49.3137 79.986552 52 76.672852 52C 73.359152 52 70.672852 49.3137 70.672852 46C 70.672852 42.6863 73.359152 40 76.672852 40C 79.986552 40 82.672852 42.6863 82.672852 46ZM 78.672852 40.5L 88.672852 10L 160.672852 10"
            stroke={quadrantColors.topRight.stroke}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 116.5272 139.306094C 128.3331 127.882194 135.6727 111.870694 135.6727 94.144994C 135.6727 59.441394 107.5399 31.308594 72.8364 31.308594C 38.1328 31.308594 10 59.441394 10 94.144994C 10 111.205794 16.7993 126.678594 27.8361 138.001394C 39.1439 127.059494 54.5491 120.326194 71.5269 120.326194C 89.1696 120.326194 105.1141 127.597194 116.5272 139.306094ZM 82.672852 46C 82.672852 49.3137 79.986552 52 76.672852 52C 73.359152 52 70.672852 49.3137 70.672852 46C 70.672852 42.6863 73.359152 40 76.672852 40C 79.986552 40 82.672852 42.6863 82.672852 46ZM 78.672852 40.5L 88.672852 10L 160.672852 10"
            stroke={quadrantColors.topRight.animatedStroke}
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Top-left (Quadrant 1) */}
        <g transform="translate(218.5, 188)">
          <path
            d="M 46.308594 92.8364C 46.308592 127.5399 74.441394 155.673 109.144994 155.673C 126.206294 155.673 141.679494 148.873 153.002594 137.836C 142.060494 126.5279 135.327194 111.1228 135.327194 94.145C 135.327194 76.5027 142.597794 60.5587 154.306594 49.1456C 142.882194 37.3396 126.870694 30 109.144994 30C 74.441394 30 46.308596 58.1328 46.308594 92.8364Z"
            fill={quadrantColors.topLeft.fill}
          />
          <path
            d="M 46.308594 92.8364C 46.308594 127.5399 74.441394 155.6727 109.144994 155.6727C 126.206294 155.6727 141.679494 148.873 153.002294 137.8357C 142.060494 126.5279 135.327194 111.1228 135.327194 94.145C 135.327194 76.5027 142.597794 60.5587 154.306094 49.1456C 142.882194 37.3396 126.870694 30 109.144994 30C 74.441394 30 46.308594 58.1328 46.308594 92.8364ZM 88 46C 88 49.3137 90.6863 52 94 52C 97.3137 52 100 49.3137 100 46C 100 42.6863 97.3137 40 94 40C 90.6863 40 88 42.6863 88 46ZM 92 40.5L 82 10L 10 10"
            stroke={quadrantColors.topLeft.stroke}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 46.308594 92.8364C 46.308594 127.5399 74.441394 155.6727 109.144994 155.6727C 126.206294 155.6727 141.679494 148.873 153.002294 137.8357C 142.060494 126.5279 135.327194 111.1228 135.327194 94.145C 135.327194 76.5027 142.597794 60.5587 154.306094 49.1456C 142.882194 37.3396 126.870694 30 109.144994 30C 74.441394 30 46.308594 58.1328 46.308594 92.8364ZM 88 46C 88 49.3137 90.6863 52 94 52C 97.3137 52 100 49.3137 100 46C 100 42.6863 97.3137 40 94 40C 90.6863 40 88 42.6863 88 46ZM 92 40.5L 82 10L 10 10"
            stroke={quadrantColors.topLeft.animatedStroke}
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>
      </svg>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-white rounded-lg shadow-sm overflow-hidden"
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        aspectRatio: `${aspectRatio}`,
      }}
    >
      {renderQuadrantShapes()}
      {renderTitle()}
      {content.steps.slice(0, 4).map((_, index) => renderNumber(index))}
      {content.steps.slice(0, 4).map((_, index) => renderQuadrantText(index))}
    </div>
  );
}
